import React from 'react';
import { SmartphoneNfc, XCircle, Loader2, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Alert, AlertDescription } from 'components/ui/alert';
import { cn } from 'lib/utils';
import { RemoteEhrStatus } from '../../types';

const statusCopy: Record<RemoteEhrStatus, { label: string; description: string }> = {
  initializing: {
    label: 'Starting session',
    description: 'Opening a secure browser session to 1177.se.',
  },
  awaiting_bankid: {
    label: 'Waiting for BankID approval',
    description: 'Scan the QR code with your BankID app to continue.',
  },
  authenticated: {
    label: 'BankID verified',
    description: 'Securely fetching your electronic health record from 1177.se. You can close this window while we finish in the background.',
  },
  completed: {
    label: 'Journal downloaded',
    description: 'Your journal has been imported into the viewer.',
  },
  failed: {
    label: 'Session failed',
    description: 'We could not complete the BankID login. Please try again.',
  },
  cancelled: {
    label: 'Session cancelled',
    description: 'The BankID login was cancelled.',
  },
};

function getStatusBadgeVariant(status: RemoteEhrStatus): string {
  if (status === 'completed') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'failed') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-primary/10 text-foreground-strong border-primary/20';
}

interface BankIdModalProps {
  isOpen: boolean;
  status: RemoteEhrStatus;
  qrImage?: string | null;
  onClose: () => void;
  onCancel: () => void;
  onRefresh?: () => void;
  error?: string | null;
  isFetchingData?: boolean;
  sessionId?: string;
}

export const BankIdModal: React.FC<BankIdModalProps> = ({
  isOpen,
  status,
  qrImage,
  onClose,
  onCancel,
  onRefresh,
  error,
  isFetchingData = false,
  sessionId,
}) => {
  const isMobileDevice = typeof window !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
    ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );

  const { label, description } = statusCopy[status];
  const canRefresh = Boolean(onRefresh) && status === 'awaiting_bankid';
  const canCancel = status === 'awaiting_bankid' || status === 'initializing';
  const showQr = !isMobileDevice && (status === 'awaiting_bankid' || status === 'initializing' || status === 'authenticated') && qrImage;
  const isSuccess = status === 'completed';
  const isFailure = status === 'failed';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="w-[min(540px,100%)] max-w-[95vw] gap-6 p-6 sm:p-8 rounded-xl"
        aria-labelledby="bankid-modal-title"
      >
        <DialogHeader className="flex flex-row items-center gap-3 text-left">
          {isSuccess ? (
            <ShieldCheck size={32} className="text-teal-700 shrink-0" />
          ) : isFailure ? (
            <XCircle size={32} className="text-red-700 shrink-0" />
          ) : (
            <SmartphoneNfc size={32} className="text-primary shrink-0" />
          )}
          <div className="space-y-0.5">
            <DialogTitle id="bankid-modal-title" className="text-xl sm:text-2xl font-semibold text-foreground-strong">
              Logga in med BankID
            </DialogTitle>
            {sessionId && (
              <span className="text-sm text-foreground-muted">Session ID: {sessionId}</span>
            )}
          </div>
        </DialogHeader>

        {showQr && (
          <div className="rounded-xl bg-card border p-5 flex items-center justify-center min-h-[220px] relative">
            {qrImage ? (
              <img
 src={qrImage}
 alt="BankID QR code"
 className="max-w-[min(260px,100%)] w-full h-auto rounded-xl border-primary/20"
 />
            ) : (
              <>
                <div
 className="w-16 h-16 rounded-full border-4 border-t-primary animate-spin"
 role="status"
 aria-label="Loading QR code"
 />
                <div className="absolute inset-3 rounded-xl border border-dashed border-primary/50 pointer-events-none animate-pulse" />
              </>
            )}
          </div>
        )}

        <div className="grid gap-1.5">
          <Badge
            variant="outline"
            className={cn(
              'inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-medium rounded-full w-fit',
              getStatusBadgeVariant(status)
            )}
          >
            {label}
          </Badge>
          <p className="text-foreground-muted text-sm leading-relaxed m-0">
            {isFetchingData && status !== 'failed'
              ? 'Importing your journal data from 1177.se…'
              : description}
          </p>
        </div>

        {showQr && (
          <ol className="m-0 pl-5 grid gap-2 text-foreground-muted text-sm list-decimal">
            <li className="leading-normal">Öppna BankID-appen på din mobil eller surfplatta.</li>
            <li className="leading-normal">Välj &quot;Skanna QR-kod&quot; och rikta kameran mot koden ovan.</li>
            <li className="leading-normal">Godkänn inloggningen i BankID-appen för att hämta din journal.</li>
          </ol>
        )}

        {isMobileDevice && (status === 'awaiting_bankid' || status === 'initializing') && (
          <div className="p-3.5 rounded-lg bg-background-soft/80 border-primary/20/50">
            <p className="m-0 text-primary font-medium text-sm">
              📱 Öppnar BankID-appen automatiskt... Om appen inte öppnas, kontrollera att BankID är installerat.
            </p>
          </div>
        )}

        {error && (
          <Alert variant="destructive" role="alert" className="flex items-center gap-2">
            <XCircle size={18} className="shrink-0" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isSuccess && (
          <div className="p-3.5 rounded-lg bg-green-50/80 border-green-200/50">
            <p className="m-0 text-green-700 font-medium text-sm">
              ✓ Your journal has been successfully loaded and is ready to view. Use the Export button in the header to download your files anytime.
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-wrap justify-end gap-3 sm:flex-row">
          {canRefresh && (
            <Button type="button" onClick={onRefresh} disabled={isFetchingData}>
              Refresh QR
            </Button>
          )}
          {canCancel && (
            <Button type="button" onClick={onCancel} disabled={isFetchingData}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isFetchingData && !isFailure && !isSuccess}
          >
            {isFetchingData && !isSuccess && !isFailure ? (
              <>
                <Loader2 size={16} className="animate-spin shrink-0" />
                Working…
              </>
            ) : isSuccess ? (
              'View journal'
            ) : (
              'Close'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
